import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useEffect, useRef } from 'react';
import { Eye, Play, MoreVertical, Settings, Edit3, BarChart3, Download, Code, Share2, Trash2, FolderInput, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@/components/ui/dropdown-menu';
import { CopyCodeModal } from '@/components/shared/CopyCodeModal';
import { getThumbnailUrl, getThumbnailSrcSet } from '@/utils/thumbnailUtils';
import { getCachedThumbnail } from '@/utils/thumbnailCache';
import { useAuth } from '@/hooks/useAuth';
import { shareVideoService } from '@/services/shareVideoService';
import { toast } from 'sonner';
import { DuplicateVideoMenuItem } from '@/components/shared/DuplicateVideoMenuItem';
import { VideoActionsDropdown } from '@/components/shared/VideoActionsDropdown';
import { HierarchicalFolder } from '@/types/hierarchicalFolders';

interface FeedVideoCardProps {
  id: string;
  title: string;
  thumbnail?: string;
  views: number;
  plays: number;
  isProcessing?: boolean;
  processingProgress?: number;
  status?: 'processing' | 'pending' | 'processed' | 'failed';
  onDelete?: (id: string) => void;
  onRename?: (id: string) => void;
  onDownload?: (id: string) => void;
  // Pastas disponíveis para "Mover para" (apenas na raiz)
  folders?: HierarchicalFolder[];
  onMoveToFolder?: (videoId: string, folderId: string) => void;
  // Remover da pasta (apenas dentro de uma pasta)
  isInsideFolder?: boolean;
  onRemoveFromFolder?: (videoId: string) => void;
  // Indica se o vídeo foi recebido via compartilhamento de outro usuário
  isSharedVideo?: boolean;
  // Quando true, o card recebe os atributos data-tour do tour guiado
  isTourTarget?: boolean;
  // Informado pelo DraggableFeedVideoCard: anula o clique durante o arraste
  isDragging?: boolean;
}

export function FeedVideoCard({
  id, title, thumbnail, views, plays,
  isProcessing, processingProgress, status,
  onDelete, onRename, onDownload,
  folders = [],
  onMoveToFolder,
  isInsideFolder = false,
  onRemoveFromFolder,
  isSharedVideo = false,
  isTourTarget = false,
  isDragging = false,
}: FeedVideoCardProps) {
  useLanguage();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [imageError, setImageError] = useState(false);
  const [showEmbedModal, setShowEmbedModal] = useState(false);
  const [cachedThumb, setCachedThumb] = useState<string | null>(null);

  // Load cached thumbnail for instant display
  useEffect(() => {
    const loadCached = async () => {
      const cached = await getCachedThumbnail(id);
      if (cached) setCachedThumb(cached);
    };
    loadCached();
  }, [id]);

  const isVideoProcessing = !isSharedVideo && (status === 'processing' || status === 'pending');

  const [showProcessingOverlay, setShowProcessingOverlay] = useState(isVideoProcessing);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const prevProcessingRef = useRef(isVideoProcessing);

  useEffect(() => {
    if (isVideoProcessing && !prevProcessingRef.current) {
      setShowProcessingOverlay(true);
      setIsAnimatingOut(false);
    } else if (!isVideoProcessing && prevProcessingRef.current) {
      setIsAnimatingOut(true);
      const timer = setTimeout(() => {
        setShowProcessingOverlay(false);
        setIsAnimatingOut(false);
      }, 400);
      return () => clearTimeout(timer);
    } else if (isVideoProcessing) {
      setShowProcessingOverlay(true);
    }
    prevProcessingRef.current = isVideoProcessing;
  }, [isVideoProcessing]);

  const formatNumber = (count: number) => count.toLocaleString(getInterfaceLanguage());

  // Só exibe "Mover para" quando há pastas disponíveis
  const hasFolders = folders.length > 0;

  return (
    <>
      <div
        {...(isTourTarget ? { 'data-tour': 'video-card-primary' } : {})}
        className={`
          group rounded-[12px] overflow-hidden
          bg-[#0B1220] h-[214px] relative
          border border-[#E7ECF3] dark:border-[rgba(15,23,42,0.08)]
          shadow-[0_1px_2px_rgba(16,24,40,0.04)]
          hover:shadow-[0_4px_14px_rgba(16,24,40,0.05)]
          hover:-translate-y-[1px]
          focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#BCD0FA]
          transition-all duration-[160ms] ease-out
          animate-fade-in
          video-card-optimized
          ${isVideoProcessing ? 'cursor-not-allowed' : 'cursor-grab active:cursor-grabbing'}
        `}
        onClick={isVideoProcessing || isDragging ? undefined : () => navigate(`/personalizations/${id}`)}
      >
        {/* Thumbnail Container */}
        <div className="absolute inset-0 bg-muted overflow-hidden thumb-container">
          {(cachedThumb || thumbnail) && !imageError ? (
            <img
              src={cachedThumb || getThumbnailUrl(thumbnail, '1280')}
              srcSet={!cachedThumb ? getThumbnailSrcSet(thumbnail) : undefined}
              alt={title}
              className="w-full h-full object-cover thumb-premium"
              loading="eager"
              decoding="async"
              onError={() => setImageError(true)}
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-[#2D7FF9]/20 to-[#2D7FF9]/5 flex items-center justify-center">
              <div className="w-12 h-12 rounded-full bg-[#2D7FF9]/10 flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-[#2D7FF9]/40" />
              </div>
            </div>
          )}

          {/* Processing Overlay */}
          {showProcessingOverlay && (
            <div
              className={`absolute inset-0 z-20 flex flex-col items-center justify-center
                transition-opacity duration-[400ms] ease-out
                backdrop-blur-[3px] rounded-[12px]
                ${isAnimatingOut ? 'opacity-0' : 'opacity-100'}
              `}
              style={{ background: 'rgba(9, 16, 28, 0.66)' }}
            >
              <div className="w-[36px] h-[36px] border-[2px] border-white/25 border-t-white/85 rounded-full animate-spin mb-3" />
              {/* Text */}
              <span className="text-white/90 text-[14px] font-semibold tracking-[0.2px]">
                {translateInterfaceText("Processando…")}</span>
              <span className="text-white/[0.72] text-[12px] font-normal mt-1">
                {translateInterfaceText("Seu vídeo está sendo processado…")}</span>
              {typeof processingProgress === 'number' && !isAnimatingOut && (
                <div className="w-[60%] h-1.5 bg-white/25 rounded-full mt-4 overflow-hidden">
                  <div
                    className="h-full bg-white/90 rounded-full transition-all duration-300"
                    style={{ width: `${processingProgress}%` }}
                  />
                </div>
              )}
            </div>
          )}

          {/* Menu de opções — para propagação do drag */}
          <div
            className="absolute top-2.5 right-2.5 z-30"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <VideoActionsDropdown
              trigger={
                <button
                  {...(isTourTarget ? { 'data-tour': 'video-actions-btn' } : {})}
                  aria-label={translateInterfaceText("Ações do vídeo")}
                  // Em touch não existe hover: o botão fica sempre visível abaixo de md
                  className="w-9 h-9 min-w-[36px] min-h-[36px] -m-1 rounded-full flex items-center justify-center transition-all duration-160 opacity-100 md:opacity-0 md:group-hover:opacity-100 md:focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#BCD0FA] cursor-pointer"
                >
                  <span className="w-7 h-7 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center hover:bg-black/60 transition-colors">
                    <MoreVertical className="w-3.5 h-3.5 text-white" />
                  </span>
                </button>
              }
              contentProps={{
                ...(isTourTarget ? { 'data-tour': 'video-actions-menu' } : {}),
                className: 'w-48 animate-scale-in',
              }}
            >
                {isVideoProcessing ? (
                  <DropdownMenuItem
                    onClick={() => onDelete?.(id)}
                    className="text-destructive data-[highlighted]:bg-red-600 data-[highlighted]:text-white"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    {translateInterfaceText("Deletar")}</DropdownMenuItem>
                ) : (
                  <>
                    <DropdownMenuItem
                      {...(isTourTarget ? { 'data-tour': 'menu-personalizar' } : {})}
                      onClick={() => navigate(`/personalizations/${id}`)}
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      {translateInterfaceText("Personalizar")}</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onRename?.(id)}>
                      <Edit3 className="w-4 h-4 mr-2" />
                      {translateInterfaceText("Renomear")}</DropdownMenuItem>
                    <DuplicateVideoMenuItem videoId={id} status={status} isShared={isSharedVideo} />
                    <DropdownMenuItem onClick={() => navigate(`/metricas/${id}`)}>
                      <BarChart3 className="w-4 h-4 mr-2" />
                      {translateInterfaceText("Métricas")}</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => window.open(`/personalizations/${id}`, '_blank', 'noopener,noreferrer')}>
                      <ExternalLink className="w-4 h-4 mr-2" />
                      {translateInterfaceText("Abrir nova aba")}</DropdownMenuItem>
                    {!isSharedVideo && (
                      <DropdownMenuItem onClick={() => onDownload?.(id)}>
                        <Download className="w-4 h-4 mr-2" />
                        {translateInterfaceText("Download")}</DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={() => setShowEmbedModal(true)}>
                      <Code className="w-4 h-4 mr-2" />
                      {translateInterfaceText("Copiar código")}</DropdownMenuItem>
                    {!isSharedVideo && (
                      <DropdownMenuItem onClick={async () => {
                        if (!user?.id_user) { toast.error(translateInterfaceText("Usuário não autenticado")); return; }
                        try {
                          const res = await shareVideoService.share(user.id_user, id);
                          if (res.success) {
                            navigator.clipboard.writeText(res.data.link);
                            toast.success(translateInterfaceText("Link de compartilhamento criado e copiado!"));
                          } else {
                            toast.error(res.message || translateInterfaceText("Erro ao compartilhar"));
                          }
                        } catch (err: any) {
                          toast.error(err?.response?.data?.message || translateInterfaceText("Erro ao compartilhar vídeo"));
                        }
                      }}>
                        <Share2 className="w-4 h-4 mr-2" />
                        {translateInterfaceText("Compartilhar")}</DropdownMenuItem>
                    )}

                    {/* Dentro da pasta: "Remover da pasta" | Na raiz: "Mover para" */}
                    {isInsideFolder ? (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => onRemoveFromFolder?.(id)}>
                          <FolderInput className="w-4 h-4 mr-2" />
                          {translateInterfaceText("Remover da pasta")}</DropdownMenuItem>
                      </>
                    ) : hasFolders && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuSub>
                          <DropdownMenuSubTrigger>
                            <FolderInput className="w-4 h-4 mr-2" />
                            {translateInterfaceText("Mover para")}</DropdownMenuSubTrigger>
                          <DropdownMenuSubContent className="w-48 max-h-60 overflow-y-auto">
                            {folders.map((folder) => (
                              <DropdownMenuItem
                                key={folder.id}
                                onClick={() => onMoveToFolder?.(id, folder.id)}
                              >
                                <span className="truncate">{folder.name}</span>
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuSubContent>
                        </DropdownMenuSub>
                      </>
                    )}

                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => onDelete?.(id)}
                      className="text-destructive data-[highlighted]:bg-red-600 data-[highlighted]:text-white"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      {translateInterfaceText("Deletar")}</DropdownMenuItem>
                  </>
                )}
            </VideoActionsDropdown>
          </div>

          {/* Overlay inferior com título e métricas */}
          <div
            className="absolute bottom-0 left-0 right-0 p-3.5 pt-12"
            style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0) 50%, rgba(0,0,0,0.72) 100%)' }}
          >
            <h3 className="text-[13px] font-semibold text-white truncate">
              {title}
            </h3>
            <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mt-1.5">
              <div className="flex items-center gap-1">
                <Eye className="w-[13px] h-[13px] text-white/90" strokeWidth={1.8} />
                <span className="text-[11px] font-medium text-white/90">
                  {formatNumber(views)} {" "}{translateInterfaceText("visualizações")}</span>
              </div>
              <div className="flex items-center gap-1">
                <Play className="w-[13px] h-[13px] text-white/90" strokeWidth={1.8} />
                <span className="text-[11px] font-medium text-white/90">
                  {formatNumber(plays)} {" "}{translateInterfaceText("plays")}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <CopyCodeModal
        isOpen={showEmbedModal}
        onClose={() => setShowEmbedModal(false)}
        entityType="video"
        entityId={id}
        entityName={title}
        userId={user?.id_user}
        videoOrigin="ORIGINAL"
      />
    </>
  );
}
